import * as React from "react";
import type { AppointmentWithContext } from "@taskiq/shared";
import { dayKeyIn, toValue, todayIn, type CalDate } from "@/lib/dates";
import type { OpenAppointment } from "@/lib/schedule";
import { cn } from "@/lib/cn";
import { EventChip } from "./EventChip";

/**
 * A month at a glance.
 *
 * Last of the four views and the least useful for dispatch, which is why a cell
 * shows what fits and a count rather than trying to hold a whole day. It answers
 * "how busy is the back half of the month" - a planning question - and hands off
 * to the week view for anything operational.
 */

/**
 * Every row is this tall, whether the month has four weeks or six. `Schedule.tsx`
 * drops `page--fill` for this view specifically so a fixed row height is safe: a
 * six-week month is simply a taller page, scrolling like every other console
 * page, rather than a shorter row squeezed to fit a viewport that a five-week
 * month already filled.
 */
const MONTH_CELL_HEIGHT = "6rem";

/**
 * One chip row: its height plus the gap under it. Pinned in `app.css` by
 * `.monthgrid__items > .event`, and the two have to agree - a chip that grows a
 * pixel there makes the arithmetic below silently wrong.
 */
const MONTH_ROW = 22;

/** The gap alone. The last row does not have one, which is the `+ GAP` below. */
const MONTH_GAP = 2;

export function MonthGrid({
  days,
  appointments,
  timezone,
  onOpen,
  onSelectDay,
}: {
  /** The full grid including the leading and trailing days of adjacent months. */
  days: CalDate[];
  appointments: AppointmentWithContext[];
  timezone: string;
  month: number;
  onOpen: OpenAppointment;
  onSelectDay: (date: CalDate) => void;
}) {
  const byDay = React.useMemo(() => {
    const map = new Map<string, AppointmentWithContext[]>();
    for (const appointment of appointments) {
      // Bucketed by start only. An appointment spanning midnight is drawn twice
      // in the week grid, where the two halves are visibly different lengths;
      // in a month cell that would read as two separate appointments.
      const key = dayKeyIn(appointment.scheduled_start, timezone);
      const list = map.get(key);
      if (list) list.push(appointment);
      else map.set(key, [appointment]);
    }
    return map;
  }, [appointments, timezone]);

  const today = toValue(todayIn(timezone));
  const currentMonth = days[Math.floor(days.length / 2)]?.m;

  /**
   * How many chips fit, measured rather than assumed.
   *
   * Every row is `MONTH_CELL_HEIGHT` regardless of the month, so this resolves
   * to the same number for every month - but it stays measured rather than
   * hard-coded, because that number is a function of `MONTH_CELL_HEIGHT`,
   * `.monthgrid__daynum`'s own height and `.monthgrid__cell`'s padding, and
   * hard-coding it is what made three the answer in the first place: the third
   * chip was clipped in half by the row's own bottom border, and a cell holding
   * exactly three had no "+N more" to admit it.
   *
   * One observer on one cell rather than forty-two: every track is the same
   * height. And no feedback loop - the box is `flex-1` inside a sized track and
   * clips, so the number of children in it cannot change its height.
   */
  const itemsRef = React.useRef<HTMLDivElement>(null);
  const [capacity, setCapacity] = React.useState(3);

  React.useLayoutEffect(() => {
    const box = itemsRef.current;
    if (!box) return;

    const measure = () =>
      setCapacity(Math.max(1, Math.floor((box.clientHeight + MONTH_GAP) / MONTH_ROW)));

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="monthgrid">
      <div className="monthgrid__head">
        {days.slice(0, 7).map((date) => (
          <div key={toValue(date)} className="monthgrid__dow">
            {new Intl.DateTimeFormat(undefined, { weekday: "short" }).format(
              new Date(date.y, date.m - 1, date.d)
            )}
          </div>
        ))}
      </div>

      {/* Four, five or six week rows depending on where the month falls, each
          exactly `MONTH_CELL_HEIGHT` - see the constant above for why a fixed
          height is the point rather than a compromise. */}
      <div
        className="monthgrid__body"
        style={{ gridTemplateRows: `repeat(${days.length / 7}, ${MONTH_CELL_HEIGHT})` }}
      >
        {days.map((date, index) => {
          const key = toValue(date);
          const items = byDay.get(key) ?? [];
          // "+N more" occupies a row of its own, so an overflowing cell shows one
          // chip fewer than it has room for. At a capacity of one it shows none:
          // a lone chip with a second one silently missing is worse than a link
          // that says two are there.
          const shown = items.length > capacity ? items.slice(0, capacity - 1) : items;
          const hidden = items.length - shown.length;

          return (
            <div
              key={key}
              className={cn(
                "monthgrid__cell",
                key === today && "monthgrid__cell--today",
                // Greyed rather than blank. A month grid that starts on a
                // Wednesday with three empty boxes reads as broken.
                date.m !== currentMonth && "monthgrid__cell--outside"
              )}
            >
              <button
                type="button"
                className="monthgrid__daynum tnum"
                onClick={() => onSelectDay(date)}
                aria-label={`Open ${new Intl.DateTimeFormat(undefined, {
                  day: "numeric",
                  month: "long",
                }).format(new Date(date.y, date.m - 1, date.d))} in the day view`}
              >
                {date.d}
              </button>

              <div className="monthgrid__items" ref={index === 0 ? itemsRef : undefined}>
                {shown.map((appointment) => (
                  <EventChip
                    key={appointment.id}
                    appointment={appointment}
                    timezone={timezone}
                    onOpen={onOpen}
                    variant="compact"
                  />
                ))}
                {hidden > 0 && (
                  <button
                    type="button"
                    className="monthgrid__more"
                    onClick={() => onSelectDay(date)}
                  >
                    +{hidden} more
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
